import { Type, type Static } from "@sinclair/typebox";

export const FicVatOptionsSchema = Type.Array(
  Type.Object(
    {
      id: Type.Integer({ minimum: 1 }),
      value: Type.Number({ minimum: 0, maximum: 100 }),
      description: Type.String(),
      isDefault: Type.Boolean(),
    },
    { additionalProperties: false },
  ),
);
export type FicVatOption = Static<typeof FicVatOptionsSchema>[number];

export const AppearanceSettingsSchema = Type.Object(
  {
    theme: Type.Union([
      Type.Literal("light"),
      Type.Literal("dark"),
      Type.Literal("system"),
    ]),
    density: Type.Union([Type.Literal("comfortable"), Type.Literal("compact")]),
    accent: Type.Union([Type.Literal("blue"), Type.Literal("teal")]),
    highContrast: Type.Boolean(),
    reducedMotion: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const FicSelectCompanySchema = Type.Object(
  { companyId: Type.String({ format: "uuid" }) },
  { additionalProperties: false },
);

export const FicSyncClientsSchema = Type.Object(
  {
    mode: Type.Union([Type.Literal("preview"), Type.Literal("apply")]),
    idempotencyKey: Type.String({ format: "uuid" }),
  },
  { additionalProperties: false },
);

export const FicSyncDocumentsSchema = Type.Object(
  {
    mode: Type.Union([Type.Literal("preview"), Type.Literal("apply")]),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2100 }),
    idempotencyKey: Type.String({ format: "uuid" }),
  },
  { additionalProperties: false },
);

export const FicReconciliationQuerySchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2100, default: 2026 }),
    ),
  },
  { additionalProperties: false },
);

export const FicReconciliationResolveSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    version: Type.Integer({ minimum: 1 }),
    jobId: Type.String({ format: "uuid" }),
  },
  { additionalProperties: false },
);

export const FicSupplierIntakeAcceptObservedSchema = Type.Object(
  {
    operationId: Type.String({ format: "uuid" }),
    version: Type.Integer({ minimum: 1 }),
    confirmed: Type.Literal(true),
  },
  { additionalProperties: false },
);

export const FicClientPushSchema = Type.Object(
  { operationId: Type.String({ format: "uuid" }) },
  { additionalProperties: false },
);

export const FicWorkReportPreviewParamsSchema = Type.Object(
  { id: Type.String({ format: "uuid" }) },
  { additionalProperties: false },
);

const FicWorkReportPreviewIssueSchema = Type.Object(
  {
    code: Type.String({ minLength: 1, maxLength: 80 }),
    message: Type.String({ minLength: 1, maxLength: 500 }),
  },
  { additionalProperties: false },
);

export const FicWorkReportPreviewSchema = Type.Object(
  {
    mode: Type.Literal("preview"),
    remoteWriteEnabled: Type.Literal(false),
    ready: Type.Boolean(),
    job: Type.Object(
      {
        id: Type.String({ format: "uuid" }),
        code: Type.String(),
        title: Type.String(),
        kind: Type.Union([
          Type.Literal("standard"),
          Type.Literal("recovery-x"),
          Type.Literal("lifeline"),
          Type.Literal("internal"),
        ]),
        accountingMode: Type.Union([
          Type.Literal("primary"),
          Type.Literal("unresolved"),
          Type.Literal("rollup"),
          Type.Literal("independent"),
        ]),
      },
      { additionalProperties: false },
    ),
    company: Type.Union([
      Type.Null(),
      Type.Object(
        {
          localId: Type.String({ format: "uuid" }),
          externalId: Type.String(),
          name: Type.String(),
          connectionReady: Type.Boolean(),
        },
        { additionalProperties: false },
      ),
    ]),
    client: Type.Union([
      Type.Null(),
      Type.Object(
        {
          localId: Type.String({ format: "uuid" }),
          externalId: Type.Union([Type.Null(), Type.String()]),
          name: Type.String(),
          vatNumber: Type.Union([Type.Null(), Type.String()]),
          taxCode: Type.Union([Type.Null(), Type.String()]),
        },
        { additionalProperties: false },
      ),
    ]),
    existingReport: Type.Union([
      Type.Null(),
      Type.Object(
        {
          externalId: Type.Union([Type.Null(), Type.String()]),
          number: Type.Union([Type.Null(), Type.String()]),
          billingStatus: Type.String(),
        },
        { additionalProperties: false },
      ),
    ]),
    document: Type.Object(
      {
        type: Type.Literal("work_report"),
        date: Type.String({ format: "date" }),
        subject: Type.String(),
        notes: Type.String(),
        revenueCenter: Type.Union([Type.Null(), Type.String()]),
        vatId: Type.Union([Type.Null(), Type.Integer({ minimum: 1 })]),
        vatPercent: Type.Union([Type.Null(), Type.String()]),
        withholdingEnabled: Type.Boolean(),
        amountNet: Type.String(),
        items: Type.Array(
          Type.Object(
            {
              description: Type.String(),
              quantity: Type.Literal("1.00"),
              unit: Type.Literal("nr"),
              netPrice: Type.String(),
            },
            { additionalProperties: false },
          ),
        ),
      },
      { additionalProperties: false },
    ),
    blockers: Type.Array(FicWorkReportPreviewIssueSchema),
    warnings: Type.Array(FicWorkReportPreviewIssueSchema),
  },
  { additionalProperties: false },
);

export type AppearanceSettings = Static<typeof AppearanceSettingsSchema>;
export type FicSelectCompany = Static<typeof FicSelectCompanySchema>;
export type FicSyncClients = Static<typeof FicSyncClientsSchema>;
export type FicSyncDocuments = Static<typeof FicSyncDocumentsSchema>;
export type FicReconciliationQuery = Static<
  typeof FicReconciliationQuerySchema
>;
export type FicReconciliationResolve = Static<
  typeof FicReconciliationResolveSchema
>;
export type FicSupplierIntakeAcceptObserved = Static<
  typeof FicSupplierIntakeAcceptObservedSchema
>;
export type FicClientPush = Static<typeof FicClientPushSchema>;
export type FicWorkReportPreviewParams = Static<
  typeof FicWorkReportPreviewParamsSchema
>;
export type FicWorkReportPreview = Static<typeof FicWorkReportPreviewSchema>;
